import { useEffect, useRef, useState } from 'react'
import type { JSX } from 'react'

import {
  Button,
  Flex,
  Heading,
  CounterInput,
  Paragraph,
  toast,
  useDebounceCallback,
} from '@buffer-mono/popcorn'

import { MODALS, useOpenGlobalModal } from '~publish/hooks/useOpenGlobalModal'
import { useSelectedChannel } from '~publish/pages/Channel/ChannelContext'
import { canPerform } from '~publish/utils/permissions/canPerform'

import { useUpdateChannelPostingGoal } from '../../hooks/useUpdateChannelPostingGoal'
import commonStyles from '../../PostingSchedule.module.css'

export function PostingGoals(): JSX.Element {
  const channel = useSelectedChannel() ?? {}
  const { postingGoal, id: channelId } = channel
  const hasEditingPrivilege = canPerform(channel, 'managePostingSchedule')
  const lastToast = useRef<string | number | null>(null)

  const [updateChannelPostingGoal] = useUpdateChannelPostingGoal()
  const openGlobalModal = useOpenGlobalModal()
  const [goal, setGoal] = useState(postingGoal?.goal ?? 0)

  useEffect(() => {
    setGoal(postingGoal?.goal ?? 0)
  }, [postingGoal])

  const submitGoal = useDebounceCallback(async (newGoal: number) => {
    try {
      const { success, error } = await updateChannelPostingGoal({
        variables: {
          input: {
            channelId,
            goal: newGoal,
          },
        },
      })

      if (!success) {
        toast.error(`Failed to set posting goal: ${error.message}`)
      }

      if (lastToast.current) {
        toast.dismiss(lastToast.current)
      }

      if (newGoal === 0) {
        lastToast.current = toast.success('Your posting goal was removed.', {
          duration: 5000,
        })
      } else {
        lastToast.current = toast.success('Your posting goal is set.', {
          duration: 5000,
        })
      }
    } catch {
      setGoal(postingGoal?.goal ?? 0)
      toast.error('Failed to set posting goal.')
    }
  }, 1000)

  useEffect(
    function executePendingGoalUpdates(): () => void {
      return () => {
        if (submitGoal.isPending()) {
          submitGoal.flush()
        }
      }
    },
    [submitGoal],
  )

  const clickSetPostingGoal = (): void => {
    openGlobalModal(MODALS.postingGoalConfiguration, {
      channelId,
      returnFocusToId: 'set-posting-goal',
    })
  }

  const handleGoalChange = (value: number): void => {
    setGoal(value)
    submitGoal(value)
  }

  return (
    <Flex
      justify="between"
      align="start"
      direction="column"
      gap="sm"
      fullWidth
      data-testid="timezone-form-wrapper"
      className={commonStyles.sectionLayout}
    >
      <Flex direction="column">
        <Heading size="small" as="h3" id="posting-goal-heading">
          Posting Goal
        </Heading>
        <Paragraph className={commonStyles.postingScheduleParagraph}>
          Choose how often you aim to post on this channel each week.
        </Paragraph>
      </Flex>
      <Flex direction="row" align="center" gap="xs">
        {postingGoal ? (
          <CounterInput
            value={goal}
            min={0}
            max={100}
            step={1}
            onChange={handleGoalChange}
            aria-labelledby="posting-goal-heading"
            disabled={!hasEditingPrivilege}
          />
        ) : (
          <Button
            id="set-posting-goal"
            variant="secondary"
            onClick={clickSetPostingGoal}
            disabled={!hasEditingPrivilege}
          >
            Set Posting Goal
          </Button>
        )}
      </Flex>
    </Flex>
  )
}
